import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useState } from 'react';

// third-party
import { Chance } from 'chance';
import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

import { getFirebaseToken } from '../../src/utils/firebaseUtils';  // Firebase utils for notifications

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';
const API_URL = process.env.REACT_APP_LOCAL_API_URL;

const chance = new Chance();

// constant
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

const verifyToken = (serviceToken) => {
    if (!serviceToken) {
        return false;
    }
    const decoded = jwtDecode(serviceToken);
    const currentTimeInSeconds = Date.now() / 1000; 
    const expirationTimeInSeconds = decoded.exp; 

    if (expirationTimeInSeconds < currentTimeInSeconds) {
        return false; 
    }

    return true;
};

// Set JWT token in Axios instance headers
const setSession = (jwtToken) => {
    if (jwtToken) {
        localStorage.setItem('jwttoken', jwtToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
    } else {
        localStorage.removeItem('jwttoken');
        delete axios.defaults.headers.common['Authorization'];
    }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);
    const [loading, setLoading] = useState(false);
    const [fcmtoken, setFCMToken] = useState(null);

    // Initialize authentication state
    useEffect(() => {
        const init = async () => {
            try {
                const jwtToken = localStorage.getItem('jwttoken');
                const empid = localStorage.getItem('empid');
                if (jwtToken && verifyToken(jwtToken)) {
                    setSession(jwtToken); 
                    dispatch({
                        type: LOGIN,
                        payload: {
                            isLoggedIn: true,
                            user: empid,
                        }
                    });
                } else {
                    dispatch({
                        type: LOGOUT
                    });
                }
            } catch (err) {
                dispatch({
                    type: LOGOUT
                });
            }

            // Handle FCM token (for push notifications)
            await gettoken();
        };

        init();
    }, []);

    const gettoken = async () => {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                const token = await getFirebaseToken(); 
                setFCMToken(token);
            } else {
                setFCMToken(null);
            }
        } catch (error) {
            setFCMToken(null);
        }
    };

    // Handle login
    const login = async (email, password, accountyear, company) => {
        setLoading(true);

        try {
            const response = await axios.post(`${API_URL}employeeregistrationform/loginpageweb`, {
                userName: email,
                password: password,
                accountyear: accountyear,
                company: company,
                token: fcmtoken,
            });

            if (response.status === 200) {
                const { data } = response.data;
                localStorage.setItem('empid', data.personaldetailId);
                localStorage.setItem('employeedesignation', data.designation);
                localStorage.setItem('masterpages', JSON.stringify(data.masterpages));
                localStorage.setItem('reportspages', JSON.stringify(data.reportspages));
                localStorage.setItem('dailyactivitypages', JSON.stringify(data.dailyactivitypages));
                localStorage.setItem('transactionpages', JSON.stringify(data.transactionpages));
                localStorage.setItem('productionpages',JSON.stringify(data.productionpages))

                localStorage.setItem('userrights', JSON.stringify(data.userrights));
                localStorage.setItem('accountyear',accountyear);
                localStorage.setItem('accountyearname',data.accountyear);
                localStorage.setItem('empname',data.empname);
                localStorage.setItem('fromdate',data.fromdate);
                localStorage.setItem('todate',data.todate);
                localStorage.setItem('jwttoken',data.jwttoken);
                //location.href="magopages/state";

                // Set JWT token in Axios headers
                setSession(data.jwttoken);

                dispatch({
                    type: LOGIN,
                    payload: {
                        isLoggedIn: true,
                        user: data.personaldetailId,
                    }
                });

                // Redirect to another page after successful login
                window.location.href = "magopages/welcome";
            }
        } catch (error) {
            console.error('Login error:', error);
        } finally {
            setLoading(false);
        }
    };

    const register = async (email, password, firstName, lastName) => {
        // Registration flow (may need to be updated)
        const id = chance.bb_pin();
        const response = await axios.post('/api/account/register', {
            id,
            email,
            password,
            firstName,
            lastName
        });
        let users = response.data;

        if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
            const localUsers = window.localStorage.getItem('users');
            users = [
                ...JSON.parse(localUsers),
                {
                    id,
                    email,
                    password,
                    name: `${firstName} ${lastName}`,
                }
            ];
        }

        window.localStorage.setItem('users', JSON.stringify(users));
    };

    // Handle logout
    const logout = () => {
        setSession(null);  // Remove JWT token from Axios headers
        localStorage.removeItem('empid');
        localStorage.removeItem('empname');
        localStorage.removeItem('jwttoken');
        dispatch({ type: LOGOUT });
        window.location.href = '/login';  // Redirect to login page
    };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <JWTContext.Provider value={{ ...state, login, logout, register }}>
            {children}
        </JWTContext.Provider>
    );
};

JWTProvider.propTypes = {
    children: PropTypes.node,
};

export default JWTContext;
